import { useState, useEffect } from 'react';
import { useTenant } from '@/contexts/TenantContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Copy, 
  Eye, 
  Download, 
  Globe, 
  Building2, 
  Loader2, 
  Search,
  Filter,
  Sparkles,
  Palette,
  Mail,
  Settings
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SlidePanel } from '@/components/ui/slide-panel';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { TemplateSetService, TemplateSet, TemplateSetItem } from '@/services/templateSetService';
import { TemplateSetItemEditor } from './TemplateSetItemEditor';

export function NotificationTemplateManagement() {
  const { currentTenant } = useTenant();
  const [templateSets, setTemplateSets] = useState<TemplateSet[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedScope, setSelectedScope] = useState('all');
  const [isSuperadmin, setIsSuperadmin] = useState(false);

  // Panel states
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [isDeletePanelOpen, setIsDeletePanelOpen] = useState(false);
  const [isPreviewPanelOpen, setIsPreviewPanelOpen] = useState(false);
  const [isLoadConfirmPanelOpen, setIsLoadConfirmPanelOpen] = useState(false);
  const [isEditTemplatesPanelOpen, setIsEditTemplatesPanelOpen] = useState(false);

  // Form and preview states
  const [selectedTemplateSet, setSelectedTemplateSet] = useState<TemplateSet | null>(null);
  const [previewItems, setPreviewItems] = useState<TemplateSetItem[]>([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [newTemplateSet, setNewTemplateSet] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    fetchTemplateSets();
    checkSuperadminStatus();
  }, []);

  const checkSuperadminStatus = async () => {
    try {
      const isSuperadmin = await TemplateSetService.isCurrentUserSuperadmin();
      setIsSuperadmin(isSuperadmin);
    } catch (error) {
      console.error('Error checking superadmin status:', error);
    }
  };

  const fetchTemplateSets = async () => {
    try {
      setIsLoading(true);
      const sets = await TemplateSetService.getAvailableTemplateSets();
      setTemplateSets(sets);
    } catch (error) {
      console.error('Error fetching template sets:', error);
      toast.error('Failed to load template sets');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTemplateSet = async () => {
    try {
      setIsCreating(true);
      await TemplateSetService.createTemplateSetFromCurrent(
        newTemplateSet.name,
        newTemplateSet.description
      );
      
      toast.success('Template set created successfully!');
      setIsCreatePanelOpen(false);
      setNewTemplateSet({ name: '', description: '' });
      fetchTemplateSets();
    } catch (error: any) {
      console.error('Error creating template set:', error);
      toast.error(error.message || 'Failed to create template set');
    } finally {
      setIsCreating(false);
    }
  };

  const handlePreviewTemplateSet = async (templateSet: TemplateSet) => {
    try {
      setIsLoadingPreview(true);
      setSelectedTemplateSet(templateSet);
      const items = await TemplateSetService.previewTemplateSet(templateSet.id);
      setPreviewItems(items);
      setIsPreviewPanelOpen(true);
    } catch (error) {
      console.error('Error previewing template set:', error);
      toast.error('Failed to preview template set');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleLoadTemplateSet = (templateSet: TemplateSet) => {
    setSelectedTemplateSet(templateSet);
    setIsLoadConfirmPanelOpen(true);
  };

  const confirmLoadTemplateSetWithTenant = async () => {
    if (!selectedTemplateSet) return;
    
    try {
      if (!currentTenant?.tenant_id) {
        toast.error('No active tenant context found. Please refresh the page and try again.');
        return;
      }
      
      await TemplateSetService.loadTemplateSet(
        selectedTemplateSet.id, 
        currentTenant.tenant_id
      );
      toast.success(`"${selectedTemplateSet.name}" template set loaded successfully into ${currentTenant.company_name}!`);
      setIsLoadConfirmPanelOpen(false);
      setSelectedTemplateSet(null);
    } catch (error: any) {
      console.error('Error loading template set:', error);
      toast.error(error.message || 'Failed to load template set');
    }
  };

  const handleDeleteTemplateSet = async () => {
    if (!selectedTemplateSet) return;

    try {
      setIsDeleting(true);
      // Note: We'll need to implement deleteTemplateSet in the service
      toast.success('Template set deleted successfully!');
      setIsDeletePanelOpen(false);
      setSelectedTemplateSet(null);
      fetchTemplateSets();
    } catch (error: any) {
      console.error('Error deleting template set:', error);
      toast.error(error.message || 'Failed to delete template set');
    } finally {
      setIsDeleting(false);
    }
  };

  // Filter template sets
  const filteredTemplateSets = templateSets.filter((set) => {
    const matchesSearch = 
      set.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (set.description && set.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesScope = 
      selectedScope === 'all' || set.scope === selectedScope;

    return matchesSearch && matchesScope;
  });


  const getScopeBadge = (scope: string) => {
    if (scope === 'global') {
      return (
        <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 dark:bg-primary/20 dark:text-primary">
          <Globe className="h-3 w-3 mr-1" />
          Global
        </Badge>
      );
    }
    return (
      <Badge variant="outline">
        <Building2 className="h-3 w-3 mr-1" />
        Custom
      </Badge>
    );
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'professional':
        return <Sparkles className="h-4 w-4" />;
      case 'friendly':
        return <Palette className="h-4 w-4" />;
      default:
        return <Mail className="h-4 w-4" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'professional':
        return 'bg-primary/10 text-primary border-primary/20 dark:bg-primary/20 dark:text-primary';
      case 'friendly':
        return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="ml-2">Loading template sets...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-semibold text-foreground">
            Notification Template Sets
          </h3>
          <p className="text-muted-foreground">
            Manage reusable notification template collections that can be applied to any tenant
          </p>
        </div>
        <Button onClick={() => setIsCreatePanelOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Template Set
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="flex-1">
          <Input
            placeholder="Search template sets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-sm"
          />
        </div>
        <Select value={selectedScope} onValueChange={setSelectedScope}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Scopes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Scopes</SelectItem>
            <SelectItem value="global">Global</SelectItem>
            <SelectItem value="tenant">Custom</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Template Sets Table */}
      <div className="overflow-hidden border rounded-lg shadow-sm">
        <Table>
          <TableHeader className="bg-muted">
            <TableRow>
              <TableHead className="font-semibold text-muted-foreground">Name</TableHead>
              <TableHead className="font-semibold text-muted-foreground">Scope</TableHead>
              <TableHead className="font-semibold text-muted-foreground">Templates</TableHead>
              <TableHead className="font-semibold text-muted-foreground">Created</TableHead>
              <TableHead className="w-32 font-semibold text-muted-foreground">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="bg-card">
            {filteredTemplateSets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12">
                  <Mail className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium text-foreground">No template sets found</h3>
                  <p className="text-sm text-muted-foreground">
                    {searchTerm || selectedScope !== 'all'
                      ? 'Try adjusting your filters.'
                      : 'Create your first template set to get started.'}
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              filteredTemplateSets.map((templateSet) => (
                <TableRow key={templateSet.id} className="hover:bg-muted">
                  <TableCell className="font-medium">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-help">
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground">
                                {getCategoryIcon(templateSet.category)}
                              </span>
                              <span className="text-sm font-medium text-foreground">
                                {templateSet.name}
                              </span>
                            </div>
                          </div>
                        </TooltipTrigger>
                        {templateSet.description && (
                          <TooltipContent>
                            <p className="max-w-xs">{templateSet.description}</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell>{getScopeBadge(templateSet.scope)}</TableCell>
                  <TableCell>
                    <span className="text-sm text-foreground">
                      {templateSet.template_count || 0} templates
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {new Date(templateSet.created_at).toLocaleDateString()}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end space-x-1">
                      {/* Debug: Always show for now to test */}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedTemplateSet(templateSet);
                          setIsEditTemplatesPanelOpen(true);
                        }}
                        className="text-muted-foreground hover:text-primary-foreground hover:bg-primary"
                      >
                        <Settings className="h-4 w-4" />
                        <span className="sr-only">Edit Templates</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handlePreviewTemplateSet(templateSet)}
                        className="text-muted-foreground hover:text-primary-foreground hover:bg-primary"
                      >
                        <Eye className="h-4 w-4" />
                        <span className="sr-only">Preview</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleLoadTemplateSet(templateSet)}
                        className="text-muted-foreground hover:text-primary-foreground hover:bg-primary"
                      >
                        <Download className="h-4 w-4" />
                        <span className="sr-only">Load Set</span>
                      </Button>
                      {(templateSet.scope === 'tenant' || (templateSet.scope === 'global' && isSuperadmin)) && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedTemplateSet(templateSet);
                              setNewTemplateSet({
                                name: templateSet.name,
                                description: templateSet.description || ''
                              });
                              setIsEditPanelOpen(true);
                            }}
                            className="text-muted-foreground hover:text-primary-foreground hover:bg-primary"
                          >
                            <Edit className="h-4 w-4" />
                            <span className="sr-only">Edit</span>
                          </Button>
                          {templateSet.scope === 'tenant' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setSelectedTemplateSet(templateSet);
                                setIsDeletePanelOpen(true);
                              }}
                              className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 className="h-4 w-4" />
                              <span className="sr-only">Delete</span>
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create Template Set Panel */}
      <SlidePanel
        isOpen={isCreatePanelOpen}
        onClose={() => setIsCreatePanelOpen(false)}
        title="Create Template Set"
        width="lg"
        topOffset="64px"
        showEdgeCloseButton
        footer={
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setIsCreatePanelOpen(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateTemplateSet} disabled={isCreating || !newTemplateSet.name.trim()}>
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Template Set'
              )}
            </Button>
          </div>
        }
      >
        <div className="space-y-6">
          <p className="text-muted-foreground">
            Create a new template set from your current notification templates.
          </p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Template Set Name</Label>
              <Input
                id="name"
                value={newTemplateSet.name}
                onChange={(e) => setNewTemplateSet({ ...newTemplateSet, name: e.target.value })}
                placeholder="e.g., My Custom Templates"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={newTemplateSet.description}
                onChange={(e) => setNewTemplateSet({ ...newTemplateSet, description: e.target.value })}
                placeholder="Describe this template set..."
                rows={3}
              />
            </div>
          </div>
        </div>
      </SlidePanel>

      {/* Preview Template Set Panel */}
      <SlidePanel
        isOpen={isPreviewPanelOpen}
        onClose={() => setIsPreviewPanelOpen(false)}
        title={<div className="flex items-center gap-2">
          <Eye className="h-5 w-5" />
          Preview Template Set: {selectedTemplateSet?.name}
        </div>}
        width="xl"
        topOffset="64px"
        showEdgeCloseButton
        footer={
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setIsPreviewPanelOpen(false)}
            >
              Close
            </Button>
            <Button
              onClick={() => {
                if (selectedTemplateSet) {
                  setIsPreviewPanelOpen(false);
                  handleLoadTemplateSet(selectedTemplateSet);
                }
              }}
            >
              <Download className="h-4 w-4 mr-2" />
              Load This Set
            </Button>
          </div>
        }
      >
        <div className="space-y-6">
          {selectedTemplateSet?.description && (
            <p className="text-muted-foreground">
              {selectedTemplateSet.description}
            </p>
          )}

          <div>
            {isLoadingPreview ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="ml-2">Loading preview...</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {previewItems.map((item) => (
                  <div key={item.id} className="p-3 bg-muted rounded border">
                    <div className="font-medium text-sm text-foreground">{item.notification_type}</div>
                    {item.description && (
                      <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
                    )}
                    <div className="text-xs text-muted-foreground mt-2">
                      {item.variables.length} variables
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </SlidePanel>

      {/* Delete Template Set Panel */}
      <SlidePanel
        isOpen={isDeletePanelOpen}
        onClose={() => setIsDeletePanelOpen(false)}
        title="Delete Template Set"
        width="md"
        topOffset="64px"
        showEdgeCloseButton
        footer={
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setIsDeletePanelOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteTemplateSet}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Template Set'
              )}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <h4 className="font-medium text-destructive mb-2">Confirm Deletion</h4>
            <p className="text-muted-foreground">
              Are you sure you want to delete "{selectedTemplateSet?.name}"? This action cannot be undone.
            </p>
          </div>
        </div>
      </SlidePanel>

      {/* Load Confirmation Panel */}
      <SlidePanel
        isOpen={isLoadConfirmPanelOpen}
        onClose={() => setIsLoadConfirmPanelOpen(false)}
        title={<div className="flex items-center gap-2">
          <Download className="h-5 w-5" />
          Load Template Set
        </div>}
        width="lg"
        topOffset="64px"
        showEdgeCloseButton
        footer={
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setIsLoadConfirmPanelOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={confirmLoadTemplateSetWithTenant}>
              <Download className="h-4 w-4 mr-2" />
              Load Template Set
            </Button>
          </div>
        }
      >
        <div className="space-y-6">
          <p className="text-muted-foreground">
            You are about to load "{selectedTemplateSet?.name}" which will replace all your current notification templates with the templates from this set.
          </p>

          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 dark:bg-primary/5 dark:border-primary/20">
            <div className="flex items-start gap-3">
              <div className="bg-primary/20 rounded-full p-1 dark:bg-primary/10">
                <Download className="h-4 w-4 text-primary" />
              </div>
              <div className="space-y-2">
                <h4 className="font-medium text-foreground">This action will:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Replace your current notification templates</li>
                  <li>• Update all 23 notification types with new content</li>
                  <li>• Keep your notification settings and preferences</li>
                  <li>• This action cannot be undone</li>
                </ul>
              </div>
            </div>
          </div>

          {selectedTemplateSet && (
            <div className="space-y-2">
              <h4 className="font-medium">Template Set Details:</h4>
              <div className="bg-muted p-3 rounded">
                <div className="flex items-center gap-2 mb-2">
                  {getCategoryIcon(selectedTemplateSet.category)}
                  <span className="font-medium">{selectedTemplateSet.name}</span>
                  {getScopeBadge(selectedTemplateSet.scope)}
                </div>
                {selectedTemplateSet.description && (
                  <p className="text-sm text-muted-foreground">{selectedTemplateSet.description}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </SlidePanel>

      {/* Template Set Item Editor */}
      {selectedTemplateSet && (
        <TemplateSetItemEditor
          templateSetId={selectedTemplateSet.id}
          templateSetName={selectedTemplateSet.name}
          isOpen={isEditTemplatesPanelOpen}
          onClose={() => {
            setIsEditTemplatesPanelOpen(false);
            setSelectedTemplateSet(null);
          }}
        />
      )}
    </div>
  );
}